/// <reference types="vite/client" />

import { acceptHMRUpdate, defineStore } from 'pinia'

import api from '@/lib/api'

import { useOrdersStore } from '@/stores/orders'
import { useQuotesStore } from '@/stores/quotes'
import { useReserveOrdersStore } from '@/stores/reserve-orders'
import { useSessionStore } from '@/stores/session'

const widgets: Record<string, WidgetItem> = {
  quotes: {
    id: 'quotes',
    title: 'Quotes',
    isActive: true,
    isOpen: false,
  },
  orders: {
    id: 'orders',
    title: 'Orders',
    isActive: true,
    isOpen: false,
  },
  recentlyShipped: {
    id: 'recentlyShipped',
    title: 'Recently Shipped',
    isActive: true,
    isOpen: false,
  },
  upcomingShipments: {
    id: 'upcomingShipments',
    title: 'Upcoming Shipments',
    isActive: true,
    isOpen: false,
  },
  reserves: {
    id: 'reserves',
    title: 'Reserves',
    isActive: true,
    isOpen: false,
  },
  inventorySearch: {
    id: 'inventorySearch',
    title: 'Inventory Search',
    isActive: true,
    isOpen: false,
  },
} as const

type WidgetIds = keyof typeof widgets

type ApiWidgetItem = {
  name: WidgetIds
  open: boolean
  active: boolean
}

type WidgetItem = {
  id: WidgetIds
  title: string
  isActive: boolean
  isOpen: boolean
}

type State = {
  isNavigationCollapsed: boolean
  isWidgetsOverlayActive: boolean
  widgets: typeof widgets
}
type Getters = {
  availableWidgets: (state: State) => Record<WidgetIds, WidgetItem>
}

type Actions = {
  toggleNavigationCollapse: (value: boolean) => void
  toggleWidgetsOverlay: (value: boolean) => void
  toggleWidget: (widgetToSet: WidgetItem) => void
  initWidgets: () => Promise<void>
  resetAllAccountPortal: () => Promise<void>
}

const getDefaultWidgets = () => JSON.parse(JSON.stringify(widgets))

export const useAccountPortalStore = defineStore<string, State, Getters, Actions>(
  'account-portal',
  {
    state: () => ({
      isNavigationCollapsed: false,
      isWidgetsOverlayActive: false,
      widgets: getDefaultWidgets(),
    }),

    getters: {
      availableWidgets: (state) => {
        const reserveOrdersStore = useReserveOrdersStore()

        const filtered = Object.entries(state.widgets).filter(([key]) => {
          return key === 'reserves' ? reserveOrdersStore.isReserveOrdersExist : true
        })
        return Object.fromEntries(filtered)
      },
    },

    actions: {
      toggleNavigationCollapse(value: boolean) {
        this.isNavigationCollapsed =
          typeof value === 'boolean' ? value : !this.isNavigationCollapsed
      },
      toggleWidgetsOverlay(value: boolean) {
        this.isWidgetsOverlayActive =
          typeof value === 'boolean' ? value : !this.isWidgetsOverlayActive
      },
      toggleWidget(widgetToSet: WidgetItem) {
        const sessionStore = useSessionStore()

        const updatedWidgets = Object.values(this.widgets).map((widget) => ({
          ...widget,
          isActive:
            widget.id === widgetToSet.id && typeof widgetToSet.isActive === 'boolean'
              ? widgetToSet.isActive
              : widget.isActive,
          isOpen:
            widget.id === widgetToSet.id && typeof widgetToSet.isOpen === 'boolean'
              ? widgetToSet.isOpen
              : widget.isOpen,
        }))

        const updatedWidgetsAsObject = updatedWidgets.reduce(
          (sum, curr) => {
            sum[curr.id] = curr
            return sum
          },
          {} as Record<WidgetIds, WidgetItem>,
        )

        api.user.updateUserInfo({
          firstName: sessionStore.user.firstName,
          lastName: sessionStore.user.lastName,
          myAccountWidgets: updatedWidgets.map((widget) => ({
            name: widget.id,
            open: widget.isOpen,
            active: widget.isActive,
          })),
        })

        this.widgets = updatedWidgetsAsObject
      },

      async initWidgets() {
        const sessionStore = useSessionStore()

        const newWidgets = getDefaultWidgets()
        const userWidgets: ApiWidgetItem[] = sessionStore.user.myAccountWidgets
        userWidgets.forEach((userWidget) => {
          const correspondingWidget = newWidgets[userWidget.name]
          if (correspondingWidget) {
            correspondingWidget.isActive = userWidget.active
            correspondingWidget.isOpen = userWidget.open
          }
        })

        this.widgets = newWidgets
      },

      async resetAllAccountPortal() {
        await this.$reset()
        const ordersStore = useOrdersStore()
        const quotesStore = useQuotesStore()
        ordersStore.$reset()
        quotesStore.$reset()
      },
    },

    persist: {
      pick: ['isNavigationCollapsed', 'widgets'],
    },
  },
)

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useAccountPortalStore, import.meta.hot))
}
